/**
 * Escape text so it renders literally inside the tooltip HTML. Defense-in-depth — the shared
 * `Tooltip.vue` renderer also runs the string through DOMPurify — but escaping here keeps labels
 * with `<`, `&`, quotes from mangling the markup.
 */
function escapeHtml(value: string): string {
  const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return value.replace(/[&<>"']/g, (ch) => map[ch]);
}

export type ChartTooltipContent = {
  /** The row/category label, e.g. a material or feature name. */
  label: string;
  /** The measured value. Numbers are `toLocaleString`-formatted; strings pass through verbatim. */
  value: number | string;
  /** Optional unit appended after the value, e.g. "kg CO₂e". */
  unit?: string;
  /** Optional series colour for the swatch (chart-derived hex/rgb). */
  color?: string;
};

/**
 * Structured HTML for a chart hover tooltip (RTLCA-4942) — a series-colour swatch, a bold label,
 * and a value·unit line. Rendered by the shared `Tooltip.vue` (`useTooltip` store), which sets the
 * dark surface + white text and sanitises via DOMPurify. Centralising this keeps every chart's
 * tooltip content identical instead of each chart hand-rolling its own ECharts formatter.
 */
export function buildChartTooltipHtml({ label, value, unit, color }: ChartTooltipContent): string {
  const valueText = typeof value === "number" ? value.toLocaleString() : value;
  const line = unit ? `${valueText} ${unit}` : valueText;
  const swatch = color ? `<div class="mb-px-6 h-px-8 w-px-24 rounded-4" style="background:${color}"></div>` : "";
  return `<div class="flex flex-col">${swatch}<span class="t-caption-bold">${escapeHtml(
    label
  )}</span><span class="t-caption">${escapeHtml(line)}</span></div>`;
}

export type ChartRowTooltipContent = {
  /** The row/category name, e.g. a construction type or material. */
  label: string;
  /** Optional second line under the heading, e.g. the model a row belongs to. */
  subtitle?: string;
  /** Every series' contribution to this row, in series order. */
  segments: { name: string; value: number; color?: string }[];
  /** The row's net total across those series. */
  total: number;
  /** Unit for the numbers, printed once on the total line. */
  unit?: string;
  /** Number formatting; defaults to `toLocaleString`. Pass the app's `format.decimalNumber` for parity. */
  formatValue?: (value: number) => string;
  /** Optional dimmed line at the bottom, e.g. the row's share of everything. */
  footnote?: string;
};

/**
 * Structured HTML for a **row-level** hover tooltip: the category, one line per contributing series
 * with its colour swatch, and the row's total.
 *
 * Used where the pointer is in a row's band rather than on one segment, which is how the bar charts
 * make thin segments reachable at all (see `utils/rowHover.ts`). Showing the breakdown here is the
 * point: the row is the thing being hovered, so the useful answer is what it is made of.
 *
 * Two deliberate choices:
 *
 *  - **Segments worth exactly 0 are left out.** They carry no ink on the chart either, and listing
 *    "A5 0.00" for every unused stage buries the ones that matter.
 *  - **The unit is printed once, on the total line.** Every line shares it, so repeating it per
 *    segment turns a short list into a wall of text.
 */
export function buildChartRowTooltipHtml({
  label,
  subtitle,
  segments,
  total,
  unit,
  formatValue,
  footnote,
}: ChartRowTooltipContent): string {
  const asText = formatValue ?? ((value: number) => value.toLocaleString());

  const lines = segments
    .filter((segment) => segment.value !== 0)
    .map((segment) => {
      const swatch = segment.color
        ? `<span class="h-px-8 w-px-8 rounded-full shrink-0" style="background:${segment.color}"></span>`
        : "";
      return `<span class="gap-px-8 flex items-center justify-between">
          <span class="gap-px-6 flex items-center">${swatch}<span class="t-caption">${escapeHtml(segment.name)}</span></span>
          <span class="t-caption">${escapeHtml(asText(segment.value))}</span>
        </span>`;
    })
    .join("");

  const totalText = unit ? `${asText(total)} ${unit}` : asText(total);

  return `<div class="gap-px-2 flex flex-col">
      <span class="t-caption-bold">${escapeHtml(label)}</span>
      ${subtitle ? `<span class="t-footnote opacity-80">${escapeHtml(subtitle)}</span>` : ""}
      ${lines}
      <span class="t-caption-bold">Total: ${escapeHtml(totalText)}</span>
      ${footnote ? `<span class="t-footnote opacity-80">${escapeHtml(footnote)}</span>` : ""}
    </div>`;
}
